import type { Seat } from "@bing/engine-core";
import { CELLS, cellAt, COLS, CONNECT, EMPTY, ROWS } from "./types";

/**
 * THE FOUR DIRECTIONS A LINE CAN RUN, as `[dCol, dRow]`.
 *
 * ⚠️ EACH ONE IS SCANNED IN **BOTH** SENSES from the landing cell, which is why there are four
 * entries and not eight: `[1, 0]` covers a run growing to the left as well as to the right.
 *
 * ⚠️ AND THE WORK IS DONE IN (row, col) SPACE, NEVER ON THE FLAT INDEX. Stepping a flat index by
 * ±1 walks off the end of a row and onto the next one, which reads as a horizontal four spanning
 * two rows — the classic Connect Four bug, and one that only shows up on the board's edges. Every
 * step here is bounds-checked per axis.
 */
export const DIRECTIONS: readonly (readonly [number, number])[] = [
  [1, 0], // ─ horizontal
  [0, 1], // │ vertical
  [1, 1], // ╱ diagonal
  [1, -1], // ╲ anti-diagonal
];

/** Every straight run of {@link CONNECT} cells on the board — the 69 windows of a 7×6 grid.
 *  Built from {@link DIRECTIONS}, so a bot's evaluation and the rules' win test cannot disagree
 *  about what a line is. */
export const ALL_LINES: readonly (readonly number[])[] = buildAllLines();

function buildAllLines(): readonly (readonly number[])[] {
  const lines: number[][] = [];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      for (const [dc, dr] of DIRECTIONS) {
        const endCol = col + dc * (CONNECT - 1);
        const endRow = row + dr * (CONNECT - 1);
        if (endCol < 0 || endCol >= COLS || endRow < 0 || endRow >= ROWS) continue;
        const line: number[] = [];
        for (let i = 0; i < CONNECT; i++) line.push(cellAt(row + dr * i, col + dc * i));
        lines.push(line);
      }
    }
  }
  return lines;
}

/** A fresh, empty board. */
export function emptyCells(): number[] {
  return new Array<number>(CELLS).fill(EMPTY);
}

/** Pieces standing in each column — i.e. the row the next piece there would land in. */
export function rebuildHeights(cells: readonly number[]): number[] {
  const heights = new Array<number>(COLS).fill(0);
  for (let col = 0; col < COLS; col++) {
    let h = 0;
    while (h < ROWS && cells[cellAt(h, col)] !== EMPTY) h++;
    heights[col] = h;
  }
  return heights;
}

/** Columns with room for another piece. */
export function openColumns(heights: readonly number[]): number[] {
  const cols: number[] = [];
  for (let col = 0; col < COLS; col++) if ((heights[col] ?? ROWS) < ROWS) cols.push(col);
  return cols;
}

/** Per column, the row a piece would land in — or `-1` when the column is full. */
export function landingRows(heights: readonly number[]): number[] {
  const rows: number[] = [];
  for (let col = 0; col < COLS; col++) {
    const h = heights[col] ?? ROWS;
    rows.push(h < ROWS ? h : -1);
  }
  return rows;
}

/** Whether every cell is taken. */
export function boardFull(heights: readonly number[]): boolean {
  for (let col = 0; col < COLS; col++) if ((heights[col] ?? ROWS) < ROWS) return false;
  return true;
}

/**
 * THE WINNING LINE THROUGH `cell`, or `null`.
 *
 * Called with the cell a piece has JUST landed in: a four can only be completed through the piece
 * that completed it, so scanning the whole board would be the same answer at 69× the cost.
 *
 * Returns the FULL run — five or six cells when the drop joined two groups — ascending, because
 * that is what the board lights. The `>= CONNECT` test (not `=== CONNECT`) is what makes a run of
 * five a win rather than a miss.
 */
export function winningLineAt(cells: readonly number[], cell: number): number[] | null {
  const owner = cells[cell];
  if (owner === undefined || owner === EMPTY) return null;
  const row = Math.floor(cell / COLS);
  const col = cell % COLS;
  for (const [dc, dr] of DIRECTIONS) {
    const run: number[] = [cell];
    // Walk BOTH senses of this direction, bounds-checking each axis on every step.
    for (const sign of [1, -1] as const) {
      let c = col + dc * sign;
      let r = row + dr * sign;
      while (c >= 0 && c < COLS && r >= 0 && r < ROWS && cells[cellAt(r, c)] === owner) {
        run.push(cellAt(r, c));
        c += dc * sign;
        r += dr * sign;
      }
    }
    if (run.length >= CONNECT) return run.sort((a, b) => a - b);
  }
  return null;
}

/**
 * IS THERE A FOUR THROUGH `cell`? — the same question as {@link winningLineAt}, answered without
 * building the line.
 *
 * ⚠️ IT EXISTS FOR THE SEARCH, NOT FOR THE RULES. The bot calls this once per node of an
 * alpha-beta tree that visits tens of thousands of positions a decision, and allocating an array
 * per node to throw it away is the difference between a tier that fits in a turn and one that
 * does not. Everything the PLAYER sees still comes from `winningLineAt`.
 *
 * ⚠️ TWO IMPLEMENTATIONS OF ONE RULE IS EXACTLY HOW A BOT ENDS UP PLAYING A DIFFERENT GAME FROM
 * THE ONE ON SCREEN, so `win-detection.test.ts` pins them EXHAUSTIVELY equivalent — every window
 * of the board, every cell of every window, plus thousands of random positions. If they ever
 * disagree that test goes red before anything ships.
 */
export function hasFourAt(cells: ArrayLike<number>, cell: number): boolean {
  const owner = cells[cell];
  if (owner === undefined || owner === EMPTY) return false;
  const row = Math.floor(cell / COLS);
  const col = cell % COLS;
  for (const [dc, dr] of DIRECTIONS) {
    let run = 1;
    for (let s = 0; s < 2; s++) {
      const sign = s === 0 ? 1 : -1;
      let c = col + (dc as number) * sign;
      let r = row + (dr as number) * sign;
      while (c >= 0 && c < COLS && r >= 0 && r < ROWS && cells[r * COLS + c] === owner) {
        run++;
        c += (dc as number) * sign;
        r += (dr as number) * sign;
      }
    }
    if (run >= CONNECT) return true;
  }
  return false;
}

/**
 * WHO OPENS HAND `hand` — the die's winner in hand 1, alternating after.
 *
 * `hand` is 1-based, so hand 1 is `firstMover` itself. THE ONE definition of the alternation:
 * a second copy of `(firstMover + hand) % 2` somewhere else is how the board and the engine end
 * up disagreeing about whose turn a new hand starts on.
 */
export function openerOf(firstMover: Seat, hand: number): Seat {
  return (firstMover + hand - 1) % 2;
}

/** The other seat. دوز is always exactly two. */
export function otherSeat(seat: Seat): Seat {
  return seat === 0 ? 1 : 0;
}
